import React, { useEffect } from "react";

import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";

import MessageAttachment from "./MessageAttachment";
import MessageText from "./MessageText";

interface Props {
  messageId: string;
  onRead: (messageId: string) => void;
  onReply: (message: string) => void;
  onEdit?: (message: string) => void;
  onDelete?: () => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3, 4),
  },
}));

const messageFragment = graphql(`
  fragment ChatMessage on Message {
    id
    chatId
    sender {
      id
      profile {
        id
        fullName
        avatarUrl(variant: THUMB)
      }
    }
    channel
    body
    attachments {
      ...ChatMessageAttachment
    }
    readAt
    createdAt
    deletedAt
    editedAt
    viewerIsSender
    parentMessageId
    parentMessage {
      id
      body
    }
  }
`);

const Message: React.FC<Props> = ({
  messageId,
  onRead,
  onReply,
  onEdit,
  onDelete,
}) => {
  const { classes } = useStyles();

  const [message] = useFragment(messageFragment, `Message:${messageId}`);

  useEffect(() => {
    if (message == null) return;

    // Since our chat pane is virtualized, this message component is in the
    // viewport when mounted. This means its safe to assume the user has read
    // this message by then, so we will set this as read on mount.
    if (!message.readAt && !message.viewerIsSender) {
      onRead(messageId);
    }
  }, [message?.readAt, message?.viewerIsSender]);

  if (message == null) return null;

  // align message on left if message comes from client
  const align = message.viewerIsSender ? "right" : "left";

  const { createdAt, readAt, body, deletedAt, editedAt, id, parentMessage } =
    message;

  return (
    <>
      {/* Make one message with multiple attachments appear as multiple messages
       * with one attachment.
       */}
      {message.attachments.map((attachment) => (
        <div key={attachment.id} className={classes.root}>
          <MessageAttachment
            align={align}
            attachment={attachment}
            createdAt={createdAt}
            isSent={true}
            isRead={!!readAt}
            isDeleted={!!deletedAt}
          />
        </div>
      ))}
      {message.body !== "" && (
        <div className={classes.root}>
          <MessageText
            align={align}
            body={body}
            createdAt={createdAt}
            deletedAt={deletedAt}
            editedAt={editedAt}
            isSent={true}
            isRead={!!readAt}
            isDeleted={!!deletedAt}
            onReply={() => onReply(id)}
            onEditMessage={onEdit}
            onDelete={onDelete}
            clientMessage={parentMessage?.body}
          />
        </div>
      )}
    </>
  );
};
export default React.memo(Message);
